
import React, { ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary Component
 * Fix: Explicitly declaring state and props members to resolve inheritance recognition issues in TypeScript.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // Added explicit property declarations to fix TS2339: Property 'state/props' does not exist on type 'ErrorBoundary'
  public state: ErrorBoundaryState;
  public props: ErrorBoundaryProps;

  // Use constructor for state initialization to ensure proper inheritance and visibility of state properties.
  constructor(props: ErrorBoundaryProps) {
    super(props);
    // Explicitly initializing state to satisfy the compiler's requirements for class members
    this.state = {
      hasError: false,
      error: null,
    };
    // Initializing props reference for strict type checking environments
    this.props = props;
  }

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to an error reporting service
    console.error('Uncaught Runtime Error:', error, errorInfo);
  }

  public render() {
    // Access this.state property inherited from React.Component to determine if an error was caught.
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8 text-center">
          <div className="max-w-md bg-white p-10 rounded-3xl shadow-2xl border border-red-100">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-4 font-serif">System Interruption</h1>
            <p className="text-slate-600 mb-8 leading-relaxed">
              We encountered a runtime exception while processing data.
            </p>
            <div className="bg-slate-50 p-4 rounded-xl mb-8 text-left border border-slate-100">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Error Signature</p>
              <p className="text-xs font-mono text-red-600 break-all">{this.state.error?.message || 'Unknown Error'}</p>
            </div>
            <button 
              onClick={() => window.location.assign('/')}
              className="w-full py-3 bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-900 transition-all"
            >
              Reload System
            </button>
          </div>
        </div>
      );
    }

    // Explicitly returning children from this.props, which is correctly typed via React.Component.
    return this.props.children;
  }
}

export default ErrorBoundary;
